import { z } from 'zod'

export const incidentSeveritySchema = z.enum(['minor', 'major', 'critical'])
export const incidentStatusSchema = z.enum([
  'investigating',
  'identified',
  'monitoring',
  'resolved',
])
export const incidentAudienceModeSchema = z.enum(['everyone', 'restricted'])
export const incidentUpdateAudienceModeSchema = z.enum(['inherit', 'restricted'])
export const incidentUpdateTypeSchema = z.enum([
  'detection',
  'observation',
  'progress',
  'mitigation',
  'resolution',
])
const ids = z
  .array(z.string().uuid())
  .max(500)
  .transform((values) => [...new Set(values)])
const allowSchema = z.object({ userIds: ids.default([]), groupIds: ids.default([]) }).strict()
export const incidentAudienceInputSchema = z
  .object({
    mode: incidentAudienceModeSchema,
    allow: allowSchema.default({ userIds: [], groupIds: [] }),
  })
  .strict()
export const incidentUpdateAudienceInputSchema = z
  .object({
    mode: incidentUpdateAudienceModeSchema,
    allow: allowSchema.default({ userIds: [], groupIds: [] }),
  })
  .strict()
export const incidentImpactInputSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('application'),
      applicationPublicId: z.string().trim().min(1).max(64),
    })
    .strict(),
  z.object({ kind: z.literal('custom'), customLabel: z.string().trim().min(1).max(80) }).strict(),
])
const core = {
  title: z.string().trim().min(1).max(180),
  descriptionMarkdown: z.string().max(50_000).default(''),
  severity: incidentSeveritySchema,
  status: incidentStatusSchema,
  startedAt: z.string().datetime({ offset: true }),
  impacts: z.array(incidentImpactInputSchema).max(100).default([]),
  ownerUserId: z.string().uuid().nullable().default(null),
  participantUserIds: ids.default([]),
  blockApplicationAccess: z.boolean().default(false),
}
export const incidentCreateInputSchema = z
  .object({ ...core, audience: incidentAudienceInputSchema.optional() })
  .strict()
export const incidentUpdateInputSchema = z
  .object({
    title: core.title.optional(),
    descriptionMarkdown: core.descriptionMarkdown.optional(),
    severity: incidentSeveritySchema.optional(),
    status: incidentStatusSchema.exclude(['resolved']).optional(),
    startedAt: core.startedAt.optional(),
    impacts: core.impacts.optional(),
    ownerUserId: z.string().uuid().nullable().optional(),
    participantUserIds: ids.optional(),
    blockApplicationAccess: z.boolean().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'au moins un champ doit être modifié')
export const incidentTimelineInputSchema = z
  .object({
    updateTypeId: z.string().uuid().optional(),
    updateType: incidentUpdateTypeSchema.optional(),
    contentMarkdown: z.string().trim().min(1).max(20_000),
    audience: incidentUpdateAudienceInputSchema.optional(),
    notify: z.boolean().default(true),
  })
  .strict()
  .refine(
    (value) => !(value.updateTypeId && value.updateType),
    'une seule nature d’évolution est autorisée',
  )
  .transform((value) =>
    value.updateTypeId || value.updateType
      ? value
      : { ...value, updateType: 'observation' as const },
  )
export const incidentTimelinePatchSchema = z
  .object({ contentMarkdown: z.string().trim().min(1).max(20_000) })
  .strict()
export const incidentUpdateTypeDefinitionInputSchema = z
  .object({
    label: z.string().trim().min(1).max(80),
    semanticType: incidentUpdateTypeSchema,
    reopensIncident: z.boolean().default(false),
    enabled: z.boolean().default(true),
    position: z.number().int().min(0).max(10_000).default(0),
  })
  .strict()
export const incidentUpdateTypeDefinitionPatchSchema = incidentUpdateTypeDefinitionInputSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'au moins un champ doit être modifié')
export const incidentListQuerySchema = z
  .object({
    status: z.enum(['active', 'resolved', 'all']).default('all'),
    severity: incidentSeveritySchema.optional(),
    search: z.string().trim().max(180).default(''),
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict()
export const incidentAdminListQuerySchema = incidentListQuerySchema
  .extend({ ownerUserId: z.string().uuid().optional() })
  .strict()

export type IncidentSeverity = z.infer<typeof incidentSeveritySchema>
export type IncidentStatus = z.infer<typeof incidentStatusSchema>
export type IncidentUpdateType = z.infer<typeof incidentUpdateTypeSchema>
export type IncidentAudienceInput = z.input<typeof incidentAudienceInputSchema>
export type IncidentUpdateAudienceInput = z.input<typeof incidentUpdateAudienceInputSchema>
export type IncidentCreateInput = z.input<typeof incidentCreateInputSchema>
export type IncidentUpdateInput = z.input<typeof incidentUpdateInputSchema>
export type IncidentTimelineInput = z.input<typeof incidentTimelineInputSchema>
export type IncidentUpdateTypeDefinitionInput = z.input<
  typeof incidentUpdateTypeDefinitionInputSchema
>
